import { keyBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { ExporterFileType, ExporterTransaction } from 'dashboard/models/exporter';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionsFilterParams } from 'dashboard/redux/actions/transactions';
import {
  ApiListRelationship,
  ApiModel,
  ApiRelationship,
  ApiRelationshipIdentifier,
  ApiResource,
  ApiResponse,
} from 'lib/interfaces/api';

type FilterByKey<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never;
}[keyof T];

// TODO: both of these seem more like utility function now that should be moved out of this?
/**
 * Return specific list relationship from JSON-API response
 * This function may be generalized to avoid repeated operation among thunk-actions
 * @param response: JSON-API response
 * @param key: key for specific relationship
 * @param nested: array of keys to fill attributes of nested relationships
 */
export const pickListRelationship = <
  Expected extends ApiResource,
  T extends ApiResponse<any> = ApiResponse<Transaction>,
  Model = T extends ApiResponse<infer P> ? P : never,
  Res = Model extends { relationships: infer Y } ? Y : never,
>(
  response: ApiResponse<any>,
  key: FilterByKey<Res, ApiListRelationship>,
  nested: string[] = [],
): Expected[] => {
  const includedItems = keyBy('id', response.included);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return response.included
    .filter((i): i is Expected =>
      response.data.relationships[key].data.some(
        (rel: ApiRelationshipIdentifier) => rel.id === i.id,
      ),
    )
    .map((expected) => {
      const relationships = (
        expected as ApiResource<unknown, Record<string, ApiListRelationship | ApiRelationship>>
      ).relationships;

      if (relationships == null) {
        return expected;
      }

      const relationshipsOverwrite = nested.reduce(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (acc, key) => ({
          ...acc,
          ...(relationships[key]?.data != null
            ? {
                [key]: {
                  data: Array.isArray(relationships[key].data)
                    ? (relationships[key] as ApiListRelationship).data.map(
                        ({ id }) => includedItems[id],
                      )
                    : includedItems[(relationships[key] as ApiRelationship).data.id],
                },
              }
            : {}),
        }),
        relationships,
      );

      return {
        ...expected,
        relationships: relationshipsOverwrite,
      };
    });
};

export const createExporterTransaction =
  ({
    currentEnvironment,
    filters,
    sortParams,
    fileType,
  }: {
    currentEnvironment: Environment;
    filters: TransactionsFilterParams;
    sortParams: SortParams;
    fileType: ExporterFileType;
  }) =>
  async () => {
    const { createdAtStart, createdAtEnd, transactionStatusIds, transactionTypeIds, omni, tagIds } =
      filters;
    const { sort, shouldSortDescending } = sortParams;

    const params = {
      'file-type': fileType,
      filter: {
        omni,
        'environment-id': currentEnvironment.id,
        'created-at-start': createdAtStart,
        'created-at-end': createdAtEnd,
        'transaction-status-ids': transactionStatusIds,
        'transaction-type-ids': transactionTypeIds,
        'tag-ids': tagIds,
      },
      sort: omni ? '' : `${shouldSortDescending ? '-' : ''}${sort}`,
    };

    await SessionClientDashboard.postResourceCreate<ExporterTransaction>({
      type: ApiModel.ExporterTransaction,
      attributes: {
        ...params,
      },
    });
  };
